import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import CreatorShell from "@/components/creator/CreatorShell";
import VoiceSteps from "@/components/sunovoice/VoiceSteps";
import PhraseStep from "@/components/sunovoice/PhraseStep";
import VoiceStatePanel from "@/components/sunovoice/VoiceStatePanel";
import SunoVoiceHeader from "@/components/sunovoice/SunoVoiceHeader";
import { getSunoVoice, isWaiting } from "@/lib/sunoVoicesApi";
import { safeApiError } from "@/lib/safeErrorText";

const POLL_MS = 4000;

/** `/create/my-voices/elpm/:id` — one Suno voice: check phrase → building → ready to pick in the Model menu. */
export default function SunoVoicePage() {
  const { id } = useParams();
  const [voice, setVoice] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(() => getSunoVoice(id).then((v) => { setVoice(v); setError(null); }).catch(setError), [id]);
  useEffect(() => { setVoice(null); load(); }, [load]);
  const waiting = isWaiting(voice);
  useEffect(() => {
    if (!waiting) return undefined;
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [waiting, load]);
  return (
    <CreatorShell testId="suno-voice-page" back="/create/my-voices" title={voice?.name || "ELPM voice"} subtitle={voice?.style || "ELPM learns your real singing voice"}>
      {error && !voice ? <p data-testid="suno-voice-not-found" className="text-[14px] text-rose-200">{safeApiError(error)}</p> : null}
      {!voice && !error ? <Loader2 className="h-6 w-6 animate-spin text-white/40" /> : null}
      {voice ? (
        <div className="max-w-3xl space-y-6 pb-20">
          <SunoVoiceHeader voice={voice} onChanged={setVoice} />
          <VoiceSteps status={voice.status} />
          {voice.status === "sing" ? <PhraseStep voice={voice} onChanged={setVoice} /> : <VoiceStatePanel voice={voice} onChanged={setVoice} />}
        </div>
      ) : null}
    </CreatorShell>
  );
}
